import type { AuthStep, AuthStepRole, CredentialField, OperationDefinition } from './erp-definition.js';
import { OPERATION_RESULT, OPERATION_VARIABLES } from './erp-definition.js';
import type { ErpOperation } from './erp.js';

/**
 * Leitura de um comando cURL (copiado da documentação ou do Postman) para preencher a definição.
 *
 * Regra de segurança: valores com cara de credencial (secret, token, senha, client_id, syndata,
 * Authorization…) NUNCA entram na definição — viram {{campo}} e cada provedor informa o seu.
 * O endereço (host) também não é gravado: ele é do provedor e vem da URL base dele.
 */

export interface ParsedCurl {
  method: string;
  url: URL;
  headers: [string, string][];
  /** Partes de --data-urlencode (sempre formulário). */
  urlencoded: string[];
  /** Partes de -d/--data/--data-raw/--data-binary. */
  raw: string[];
  user: string | null;
  getMode: boolean;
  warnings: string[];
}

/** Separa os argumentos como um shell faria (aspas simples/duplas, continuação com \ ou ^). */
export function tokenize(cmd: string): string[] {
  const text = cmd.replace(/\\\r?\n/g, ' ').replace(/\^\r?\n/g, ' ').replace(/`\r?\n/g, ' ');
  const out: string[] = [];
  let cur = '';
  let quote: '"' | "'" | null = null;
  let has = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quote) {
      if (ch === quote) quote = null;
      else if (ch === '\\' && quote === '"' && i + 1 < text.length && '"\\$`'.includes(text[i + 1]!)) cur += text[++i];
      else cur += ch;
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      has = true;
      continue;
    }
    if (/\s/.test(ch)) {
      if (has || cur) out.push(cur);
      cur = '';
      has = false;
      continue;
    }
    if (ch === '\\' && i + 1 < text.length) {
      cur += text[++i];
      has = true;
      continue;
    }
    cur += ch;
    has = true;
  }
  if (quote) throw new Error('Aspas sem fechamento no comando');
  if (has || cur) out.push(cur);
  return out;
}

const NO_VALUE = new Set(['-L', '--location', '-k', '--insecure', '-s', '--silent', '-S', '--show-error', '-v', '--verbose', '-i', '--include', '--compressed', '-f', '--fail', '-g', '--globoff']);
const IGNORE_WITH_VALUE = new Set(['-o', '--output', '-m', '--max-time', '--connect-timeout', '-w', '--write-out', '-A', '--user-agent', '-e', '--referer', '--retry', '-b', '--cookie', '-c', '--cookie-jar']);

export function parseCurl(cmd: string): ParsedCurl {
  const args = tokenize(cmd.trim());
  if (!args.length || args[0]!.toLowerCase() !== 'curl') throw new Error('O texto precisa começar com "curl"');
  let method: string | null = null;
  let url: string | null = null;
  const headers: [string, string][] = [];
  const urlencoded: string[] = [];
  const raw: string[] = [];
  let user: string | null = null;
  let getMode = false;
  const warnings: string[] = [];
  for (let i = 1; i < args.length; i++) {
    const a = args[i]!;
    const next = () => {
      const v = args[++i];
      if (v === undefined) throw new Error(`Falta o valor de ${a}`);
      return v;
    };
    // forma --opcao=valor
    const eq = a.startsWith('--') ? a.indexOf('=') : -1;
    const name = eq > 0 ? a.slice(0, eq) : a;
    const inline = eq > 0 ? a.slice(eq + 1) : null;
    const value = () => inline ?? next();
    if (NO_VALUE.has(name)) {
      if (name === '-k' || name === '--insecure') warnings.push('A opção --insecure foi ignorada: a plataforma sempre valida o certificado HTTPS.');
      continue;
    }
    if (IGNORE_WITH_VALUE.has(name)) {
      value();
      continue;
    }
    switch (name) {
      case '-X':
      case '--request':
        method = value().toUpperCase();
        break;
      case '-H':
      case '--header': {
        const h = value();
        const idx = h.indexOf(':');
        if (idx > 0) headers.push([h.slice(0, idx).trim(), h.slice(idx + 1).trim()]);
        break;
      }
      case '--data-urlencode':
        urlencoded.push(value());
        break;
      case '-d':
      case '--data':
      case '--data-raw':
      case '--data-binary':
      case '--data-ascii':
      case '--json':
        if (name === '--json') headers.push(['Content-Type', 'application/json']);
        raw.push(value());
        break;
      case '-F':
      case '--form':
        warnings.push('Envio de arquivos (-F) não é suportado e foi ignorado.');
        value();
        break;
      case '-u':
      case '--user':
        user = value();
        break;
      case '-G':
      case '--get':
        getMode = true;
        break;
      case '--url':
        url = value();
        break;
      default:
        if (/^https?:\/\//i.test(a)) url = a;
        else if (a.startsWith('-')) warnings.push(`Opção ignorada: ${a}`);
        else if (!url) url = a;
    }
  }
  if (!url) throw new Error('Nenhum endereço (URL) encontrado no comando');
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`Endereço inválido: ${url}`);
  }
  const hasData = urlencoded.length > 0 || raw.length > 0;
  return { method: method ?? (hasData && !getMode ? 'POST' : 'GET'), url: parsed, headers, urlencoded, raw, user, getMode, warnings };
}

/* ───────────── conversão para a definição ───────────── */

const SECRET_KEY = /(secret|senha|password|passwd|pass$|token|syndata|api[_-]?key|apikey|chave|signature|assinatura)/i;
const ID_KEY = /^(client[_-]?id|username|user|usuario|usuário|login|account|conta)$/i;
/** Valores que vêm de uma etapa anterior do fluxo (variáveis de sessão), não do provedor. */
const SESSION_KEY = /^(access_token|refresh_token|id_token)$/i;
/** Parâmetros de protocolo que costumam ser fixos. */
const FIXED_KEY = /^(grant_type|scope|audience|resource|response_type)$/i;

const snake = (k: string) =>
  k
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'c_$1')
    .slice(0, 40) || 'campo';

const labelOf = (k: string) => k.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export interface CurlRequestParts {
  method: 'GET' | 'POST' | 'PUT';
  path: string;
  /** URL absoluta com {{host}} (para etapas de autenticação). */
  absoluteUrl: string;
  query: Record<string, string>;
  headers: Record<string, string>;
  bodyFormat: 'json' | 'form';
  body: string | null;
  form: Record<string, string>;
}

export interface CurlImport {
  request: CurlRequestParts;
  /** Autenticação Basic detectada (-u ou Authorization: Basic). */
  basicAuth: boolean;
  credentialFields: CredentialField[];
  /** O que foi feito, em português, para a equipe revisar. */
  notes: string[];
}

class Collector {
  fields = new Map<string, CredentialField>();
  notes: string[] = [];
  credential(key: string, secret: boolean): string {
    const k = snake(key);
    if (!this.fields.has(k)) this.fields.set(k, { key: k, label: labelOf(key), secret, hint: '', kind: 'text' });
    return `{{${k}}}`;
  }
  /** Decide se o valor é credencial ou variável de sessão; devolve o texto a gravar na definição. */
  value(key: string, value: string, where: string): string {
    if (SESSION_KEY.test(key)) {
      this.notes.push(`${where} "${key}" virou {{${snake(key)}}}, valor obtido por uma etapa anterior do fluxo. O valor colado foi descartado.`);
      return `{{${snake(key)}}}`;
    }
    if (FIXED_KEY.test(key)) return value;
    if (SECRET_KEY.test(key)) {
      this.notes.push(`${where} "${key}" virou o campo de credencial {{${snake(key)}}} (segredo). O valor colado foi descartado.`);
      return this.credential(key, true);
    }
    if (ID_KEY.test(key)) {
      this.notes.push(`${where} "${key}" virou o campo de credencial {{${snake(key)}}}. O valor colado foi descartado.`);
      return this.credential(key, false);
    }
    return value;
  }
}

function splitForm(parts: string[]): [string, string][] {
  const out: [string, string][] = [];
  for (const p of parts) {
    for (const piece of p.split('&')) {
      const i = piece.indexOf('=');
      if (i <= 0) continue;
      let v = piece.slice(i + 1);
      try {
        v = decodeURIComponent(v.replace(/\+/g, ' '));
      } catch {
        /* mantém como está */
      }
      out.push([piece.slice(0, i), v]);
    }
  }
  return out;
}

const DROP_HEADERS = /^(content-type|content-length|accept|accept-encoding|user-agent|host|connection|cookie|postman-token|cache-control)$/i;

/** Substitui documentos conhecidos pelas variáveis da operação. */
function operationVar(op: ErpOperation | null, value: string): string | null {
  if (!op) return null;
  const vars = OPERATION_VARIABLES[op].map((v) => v.key);
  const digits = value.replace(/\D/g, '');
  if (vars.includes('documento') && (digits.length === 11 || digits.length === 14) && /^[\d.\-/]+$/.test(value)) {
    return value === digits ? '{{documento}}' : '{{documento_formatado}}';
  }
  return null;
}

function rewriteJson(text: string, c: Collector, op: ErpOperation | null): string {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    c.notes.push('O corpo não é um JSON válido e foi mantido como texto; revise.');
    return text;
  }
  const walk = (v: unknown, key: string): unknown => {
    if (Array.isArray(v)) return v.map((x) => walk(x, key));
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x, k)]));
    if (typeof v === 'string' || typeof v === 'number') {
      const s = String(v);
      const replaced = c.value(key, s, 'Campo do corpo');
      if (replaced !== s) return replaced;
      const asVar = operationVar(op, s);
      if (asVar) {
        c.notes.push(`Campo do corpo "${key}" virou ${asVar}.`);
        return asVar;
      }
    }
    return v;
  };
  return JSON.stringify(walk(data, ''), null, 2);
}

/**
 * Converte um cURL em requisição. Serve para QUALQUER chamada: etapa de autenticação
 * (login, refresh…) ou operação. Credenciais viram campos, tokens viram variáveis de sessão.
 */
export function curlToRequest(p: ParsedCurl, op: ErpOperation | null = null): CurlImport {
  const c = new Collector();
  let basicAuth = false;
  const headers: Record<string, string> = {};
  for (const [k, v] of p.headers) {
    if (/^authorization$/i.test(k)) {
      if (/^bearer\s/i.test(v)) {
        headers.Authorization = 'Bearer {{access_token}}';
        c.notes.push('Authorization: Bearer virou "Bearer {{access_token}}" (token obtido no fluxo de autenticação). O token colado foi descartado.');
      } else if (/^basic\s/i.test(v)) {
        basicAuth = true;
        c.credential('usuario', false);
        c.credential('senha', true);
        c.notes.push('Authorization: Basic detectado: autenticação Basic com os campos {{usuario}} e {{senha}}. O valor colado foi descartado.');
      } else {
        headers.Authorization = c.credential('authorization', true);
        c.notes.push('Authorization virou o campo de credencial {{authorization}}. O valor colado foi descartado.');
      }
      continue;
    }
    if (DROP_HEADERS.test(k)) continue;
    headers[k] = c.value(k, v, 'Cabeçalho');
  }
  if (p.user) {
    basicAuth = true;
    c.credential('usuario', false);
    c.credential('senha', true);
    c.notes.push('Usuário e senha (-u) viraram autenticação Basic com {{usuario}} e {{senha}}. Os valores colados foram descartados.');
  }

  const query: Record<string, string> = {};
  for (const [k, v] of p.url.searchParams) {
    const replaced = c.value(k, v, 'Parâmetro');
    const asVar = replaced === v ? operationVar(op, v) : null;
    if (asVar) c.notes.push(`Parâmetro "${k}" virou ${asVar}.`);
    query[k] = asVar ?? replaced;
  }

  const method = (['GET', 'POST', 'PUT'].includes(p.method) ? p.method : 'POST') as CurlRequestParts['method'];
  if (method !== p.method) c.notes.push(`Método ${p.method} não suportado; ficou POST.`);
  const isForm = p.urlencoded.length > 0 || p.headers.some(([k, v]) => /^content-type$/i.test(k) && /x-www-form-urlencoded/i.test(v));
  let bodyFormat: CurlRequestParts['bodyFormat'] = 'json';
  let body: string | null = null;
  let form: Record<string, string> = {};
  if (p.getMode) {
    for (const [k, v] of splitForm([...p.urlencoded, ...p.raw])) query[k] = c.value(k, v, 'Parâmetro');
  } else if (isForm) {
    bodyFormat = 'form';
    form = Object.fromEntries(splitForm([...p.urlencoded, ...p.raw]).map(([k, v]) => [k, operationVar(op, v) ?? c.value(k, v, 'Campo do formulário')]));
  } else if (p.raw.length) {
    body = rewriteJson(p.raw.join(''), c, op);
  }
  const port = p.url.port ? `:${p.url.port}` : '';
  const path = p.url.pathname || '/';
  c.notes.unshift(`Endereço: ${p.url.protocol}//{{host}}${port}${path}. O host (${p.url.hostname}) não foi gravado: cada provedor informa o seu na URL base.`);
  if (p.url.protocol !== 'https:') c.notes.push('Atenção: o cURL usa http://; a plataforma exige https://.');
  return {
    request: { method, path, absoluteUrl: `https://{{host}}${port}${path}`, query, headers, bodyFormat, body, form },
    basicAuth,
    credentialFields: [...c.fields.values()],
    notes: [...c.notes, ...p.warnings],
  };
}

/** Saídas sugeridas para etapas de autenticação (padrão OAuth2); a equipe ajusta conforme a resposta real. */
export const SUGGESTED_AUTH_OUTPUTS: Record<string, string> = { access_token: 'access_token', refresh_token: 'refresh_token', expires_in: 'expires_in' };

/** Etapa de autenticação a partir de um cURL. */
export function curlToAuthStep(p: ParsedCurl, step: { key: string; name: string; role: AuthStepRole }): { step: AuthStep; imported: CurlImport } {
  const imported = curlToRequest(p, null);
  const r = imported.request;
  imported.notes.push('Valores extraídos sugeridos: access_token, refresh_token e expires_in. Confira com uma resposta real e ajuste.');
  return {
    step: {
      key: step.key,
      name: step.name,
      role: step.role,
      method: r.method,
      url: r.absoluteUrl,
      query: r.query,
      headers: r.headers,
      bodyFormat: r.bodyFormat,
      body: r.body,
      form: r.form,
      outputs: { ...SUGGESTED_AUTH_OUTPUTS },
      fixed: [],
    },
    imported,
  };
}

/** Requisição de uma operação a partir de um cURL (a normalização da operação é preservada por quem chama). */
export function curlToOperation(p: ParsedCurl, op: ErpOperation): { operation: Partial<OperationDefinition>; imported: CurlImport } {
  const imported = curlToRequest(p, op);
  const r = imported.request;
  imported.notes.push(`Agora configure em Normalização onde estão os campos na resposta (${OPERATION_RESULT[op].fields.length} campos).`);
  return { operation: { method: r.method, path: r.path, query: r.query, headers: r.headers, bodyFormat: r.bodyFormat, body: r.body, form: r.form }, imported };
}
